/*
 * <lh-partner-carousel>: two animation styles behind one element.
 *
 * "paged" is a Slyde carousel with a segmented progress rail, modeled on
 * assets/hero-carousel.js. Autoplay is driven here rather than by Slyde's
 * Autoplay module so the timer restarts on every advance, however the slide
 * changed (timer, drag or keyboard).
 *
 * Centring is Slyde's own `padding`, not a transform on top of it: it places
 * the active slide at paddingStart, so `calc(50% - halfRow)` crops the row
 * evenly at both edges and re-resolves on resize. The CSS mirrors the same
 * numbers so the first paint is already right, then hands over on [data-ready].
 *
 * "free" does not touch Slyde at all. Slyde's freeMode still clamps to a finite
 * translate range even with `loop`, and has no notion of drifting on its own,
 * so the row is repeated until it tiles the viewport and translated from a
 * single rAF loop that carries both the marquee drift and the drag momentum.
 */

(() => {

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/* Momentum left after one second of coasting; the fling dies in ~0.5s. */
const FRICTION = 0.0015;
const MAX_FLING = 2400;
/* A release this long after the last move is a stop, not a throw. */
const FLING_WINDOW = 80;

class PartnerCarousel extends HTMLElement {
  #mode = 'paged';

  // Paged
  #slyde = null;
  #timer = null;
  #segment = null;
  #media = null;
  #mediaWide = null;
  #total = 0;
  #autoplay = false;
  #speed = 5;

  // Free scroll
  #track = null;
  #slides = [];
  #tileWidth = 0;
  #width = 0;
  #offset = 0;
  #velocity = 0;
  #frame = null;
  #stamp = 0;
  #pointer = null;
  #dragged = false;
  #marquee = false;
  #marqueeSpeed = 40;
  #pauseOnHover = true;
  #paused = false;
  #onscreen = true;
  #visible = true;
  #resize = null;
  #intersect = null;

  connectedCallback() {
    requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    this.removeAttribute('data-ready');
    document.removeEventListener('visibilitychange', this.#onVisibility);

    this.#stop();
    this.#media?.removeEventListener('change', this.#onBreakpoint);
    this.#mediaWide?.removeEventListener('change', this.#onBreakpoint);
    if (this.#slyde) {
      try {
        this.#slyde.destroy();
      } catch (e) {
        /* Slyde throws if it never finished initialising; nothing to clean up. */
      }
      this.#slyde = null;
    }

    this.#halt();
    this.#resize?.disconnect();
    this.#intersect?.disconnect();
    this.#resize = null;
    this.#intersect = null;
  }

  #init() {
    this.#mode = this.dataset.animation === 'free' ? 'free' : 'paged';

    if (this.#mode === 'free') {
      this.#initFree();
    } else {
      this.#initPaged();
    }
  }

  /* ---------------------------------------------------------------- paged */

  #initPaged() {
    const { Slyde } = window.Slyde || {};
    if (!Slyde) return;

    const wrapper = this.querySelector('.slyde-wrapper');
    if (!wrapper) return;

    const slides = wrapper.querySelectorAll('.slyde-slide');
    this.#total = slides.length;
    if (this.#total <= 1) {
      this.querySelector('.lh-partner-carousel__pagination')?.setAttribute('hidden', '');
      return;
    }

    this.#segment = this.querySelector('.lh-partner-carousel__segment');
    this.#autoplay = this.dataset.autoplay === 'true' && !window.matchMedia(REDUCED_MOTION).matches;
    this.#speed = parseInt(this.dataset.speed, 10) || 5;

    this.style.setProperty('--lh-pc-count', String(this.#total));

    const num = (key, fallback) => parseFloat(this.dataset[key]) || fallback;
    const gapMobile = num('gapMobile', 16);
    const gapDesktop = num('gap', 32);

    this.#slyde = new Slyde(this, {
      loop: true,
      navigation: false,
      pagination: false,
      thumbnails: false,
      video: false,
      freeMode: false,
      padding: this.#padding(),
      slidesPerView: num('cardsMobile', 3),
      spaceBetween: gapMobile,
      breakpoints: {
        741: { slidesPerView: num('cardsTablet', 3), spaceBetween: gapDesktop },
        1200: { slidesPerView: num('cardsDesktop', 5), spaceBetween: gapDesktop },
      },
    });

    this.#slyde.on('slideChange', () => this.#onSlideChange());
    this.#onSlideChange();

    // The CSS pre-layout and Slyde's own offset would otherwise stack.
    this.setAttribute('data-ready', '');

    // slidesPerView and spaceBetween ride Slyde's `breakpoints`, but `padding`
    // does not, so the row's half-width has to be swapped by hand. The value is
    // cached against container width, which has just changed, so update() picks
    // it up.
    this.#media = window.matchMedia('(min-width: 741px)');
    this.#mediaWide = window.matchMedia('(min-width: 1200px)');
    this.#media.addEventListener('change', this.#onBreakpoint);
    this.#mediaWide.addEventListener('change', this.#onBreakpoint);

    if (this.#autoplay) {
      this.#start();
      this.addEventListener('pointerenter', this.#stop);
      this.addEventListener('pointerleave', this.#start);
      this.addEventListener('focusin', this.#stop);
      this.addEventListener('focusout', this.#start);
      document.addEventListener('visibilitychange', this.#onVisibility);
    }
  }

  #onSlideChange() {
    const raw = this.#slyde?.currentIndex ?? 0;
    const index = ((raw % this.#total) + this.#total) % this.#total;

    this.#segment?.style.setProperty('--lh-pc-index', String(index));

    // Slyde only marks bullets and thumbs, never slides, and its loop clones
    // have to be skipped so the index still lines up with the authored blocks.
    this.#originals().forEach((slide, i) => slide.classList.toggle('is-active', i === index));

    if (this.#autoplay && this.#timer) this.#start();
  }

  #originals() {
    return Array.from(this.querySelectorAll('.slyde-slide:not(.slyde-slide-clone)'));
  }

  /*
   * Centre the row: Slyde places the active slide at paddingStart, so pulling
   * it back by half the row's width less half the container crops the row
   * evenly at both edges. Expressed as a calc so Slyde re-resolves it against
   * the container on every resize.
   */
  #padding() {
    const half = window.matchMedia('(min-width: 1200px)').matches
      ? parseFloat(this.dataset.halfDesktop)
      : window.matchMedia('(min-width: 741px)').matches
        ? parseFloat(this.dataset.halfTablet)
        : parseFloat(this.dataset.halfMobile);

    return isNaN(half) ? 0 : `calc(50% - ${half}px)`;
  }

  #onBreakpoint = () => {
    if (!this.#slyde) return;
    this.#slyde.settings.padding = this.#padding();
    this.#slyde.update();
  };

  #start = () => {
    this.#stop();
    if (!this.#autoplay) return;
    this.#timer = setInterval(() => this.#slyde?.next(), this.#speed * 1000);
  };

  #stop = () => {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  };

  /* ----------------------------------------------------------- free scroll */

  #initFree() {
    const track = this.querySelector('.lh-partner-carousel__track');
    if (!track) return;

    this.#track = track;
    this.#slides = Array.from(track.children);
    if (!this.#slides.length) return;

    this.#marquee = this.dataset.marquee === 'true' && !window.matchMedia(REDUCED_MOTION).matches;
    this.#marqueeSpeed = parseFloat(this.dataset.marqueeSpeed) || 40;
    this.#pauseOnHover = this.dataset.marqueePause === 'true';

    this.#tileTrack();
    this.setAttribute('data-ready', '');

    track.addEventListener('pointerdown', this.#onPointerDown);
    track.addEventListener('pointermove', this.#onPointerMove);
    track.addEventListener('pointerup', this.#onPointerUp);
    track.addEventListener('pointercancel', this.#onPointerUp);
    // A drag that ends over a card must not also click through to it.
    track.addEventListener('click', this.#onClick, true);
    track.addEventListener('dragstart', this.#onDragStart);

    if (this.#pauseOnHover) {
      this.addEventListener('pointerenter', this.#pause);
      this.addEventListener('pointerleave', this.#resume);
    }
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibility);

    // Re-tiling mutates the track, so only react to a width that really moved.
    this.#resize = new ResizeObserver(() => {
      if (this.offsetWidth === this.#width) return;
      this.#tileTrack();
    });
    this.#resize.observe(this);

    this.#intersect = new IntersectionObserver((entries) => {
      this.#onscreen = entries.some((entry) => entry.isIntersecting);
      this.#play();
    });
    this.#intersect.observe(this);

    this.#play();
  }

  /*
   * Repeat the authored run until it covers the viewport plus one full run, so
   * wrapping the offset by one run width never exposes a gap. The trailing flex
   * gap is part of the tile; without it the seam is one gap short.
   */
  #tileTrack() {
    const track = this.#track;
    if (!track) return;

    track.querySelectorAll('[data-lh-clone]').forEach((node) => node.remove());

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const tile = track.getBoundingClientRect().width + gap;
    if (!tile) return;

    this.#tileWidth = tile;
    this.#width = this.offsetWidth;

    const target = tile + this.#width;
    for (let width = tile; width < target; width += tile) {
      for (const slide of this.#slides) {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-lh-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        // Editor hooks and ids must stay unique to the authored blocks.
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        track.appendChild(clone);
      }
    }

    this.#wrap();
    this.#apply();
  }

  #wrap() {
    if (!this.#tileWidth) return;
    this.#offset = ((this.#offset % this.#tileWidth) + this.#tileWidth) % this.#tileWidth;
  }

  #apply() {
    if (this.#track) this.#track.style.transform = `translate3d(${-this.#offset}px, 0, 0)`;
  }

  #drifting() {
    return this.#marquee && !this.#paused && this.#onscreen && this.#visible;
  }

  #moving() {
    return !this.#pointer && (Math.abs(this.#velocity) > 1 || this.#drifting());
  }

  #play = () => {
    if (this.#frame !== null || !this.#moving()) return;
    this.#stamp = performance.now();
    this.#frame = requestAnimationFrame(this.#tick);
  };

  #halt() {
    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }
  }

  #tick = (now) => {
    // Clamped so a backgrounded tab does not resume with one huge jump.
    const delta = Math.min((now - this.#stamp) / 1000, 0.05);
    this.#stamp = now;

    if (Math.abs(this.#velocity) > 1) {
      this.#offset += this.#velocity * delta;
      this.#velocity *= Math.pow(FRICTION, delta);
    } else {
      this.#velocity = 0;
      if (this.#drifting()) this.#offset += this.#marqueeSpeed * delta;
    }

    this.#wrap();
    this.#apply();

    this.#frame = this.#moving() ? requestAnimationFrame(this.#tick) : null;
  };

  #pause = () => {
    this.#paused = true;
  };

  #resume = () => {
    this.#paused = false;
    this.#play();
  };

  #onPointerDown = (event) => {
    if (event.button > 0) return;

    this.#halt();
    this.#velocity = 0;
    this.#dragged = false;
    this.#pointer = {
      id: event.pointerId,
      origin: event.clientX,
      offset: this.#offset,
      x: event.clientX,
      stamp: performance.now(),
      travelled: 0,
    };

    this.#track.setPointerCapture(event.pointerId);
    this.setAttribute('data-dragging', '');
  };

  #onPointerMove = (event) => {
    const drag = this.#pointer;
    if (!drag || event.pointerId !== drag.id) return;

    const now = performance.now();
    const dx = event.clientX - drag.x;
    const elapsed = Math.max(now - drag.stamp, 1);

    // Same sign convention as #offset: dragging left scrolls forward.
    const instant = (-dx / elapsed) * 1000;
    this.#velocity = this.#velocity * 0.7 + instant * 0.3;

    drag.x = event.clientX;
    drag.stamp = now;
    drag.travelled += Math.abs(dx);

    this.#offset = drag.offset - (event.clientX - drag.origin);
    this.#wrap();
    this.#apply();
  };

  #onPointerUp = (event) => {
    const drag = this.#pointer;
    if (!drag || event.pointerId !== drag.id) return;

    this.#pointer = null;
    this.removeAttribute('data-dragging');
    try {
      this.#track.releasePointerCapture(event.pointerId);
    } catch (e) {
      /* Capture is already gone on pointercancel. */
    }

    if (performance.now() - drag.stamp > FLING_WINDOW) this.#velocity = 0;
    this.#velocity = Math.max(-MAX_FLING, Math.min(MAX_FLING, this.#velocity));
    this.#dragged = drag.travelled > 5;

    this.#play();
  };

  #onClick = (event) => {
    if (!this.#dragged) return;
    this.#dragged = false;
    event.preventDefault();
    event.stopPropagation();
  };

  #onDragStart = (event) => {
    event.preventDefault();
  };

  /* ---------------------------------------------------------------- shared */

  #onVisibility = () => {
    if (this.#mode === 'free') {
      this.#visible = !document.hidden;
      this.#play();
      return;
    }

    if (document.hidden) {
      this.#stop();
    } else {
      this.#start();
    }
  };

  next() {
    this.#slyde?.next();
  }

  prev() {
    this.#slyde?.prev();
  }

  goTo(index) {
    this.#slyde?.goTo(index);
  }
}

if (!customElements.get('lh-partner-carousel')) {
  customElements.define('lh-partner-carousel', PartnerCarousel);
}

})();
